import React, { forwardRef } from 'react';
import { Modal, SnackBar } from '@umu/umu-design';
import { lang } from '@umu/ukit-lang';
import '../../style/caet/share.scss';
import { PermissionControl } from './components/permissionControl';
import { CertificatePermission } from './interface/api';
import { useCaetShare } from './hook/useCaetShare';
import { SHARE_ICON_SCROLL_THRESHOLD, defaultCaetShareConfirm } from './dataFlow';
import { ICaetShareProps, ICaetShareRef, ICaetShareDialogProps, IShareIconProps } from './interface/share';
import { ReactIcon } from 'common/src/components/reactIcon';

const { confirm } = Modal;

/**
 * ShareIcon 原子组件
 * 用于展示单个分享图标
 */
const ShareIcon: React.FC<IShareIconProps> = ({ iconItem, isSelected, onClick }) => {
    const handleClick = (): void => {
        onClick(iconItem.key);
    };

    return (
        <div className={`share-icon-item ${isSelected ? 'selected' : ''}`} onClick={handleClick}>
            <div className={`share-icon ${iconItem.icon}`}>
                <ReactIcon type={iconItem.icon} size={32} iconSrc={iconItem.src} />
            </div>
            <span className="icon-label">{iconItem.label}</span>
        </div>
    );
};

const CaetShare = forwardRef<ICaetShareRef, ICaetShareProps>(
    ({ link, showShareIcon = false, iconList, onPermission, multiple = false, iconClickType, permission }, ref) => {
        // 使用自定义 Hook 管理逻辑
        const {
            info,
            displayIconList,
            shouldShowIcons,
            handlePermissionChange,
            handleIconClick,
            handleCopyLink,
            displayLink,
        } = useCaetShare({
            link,
            showShareIcon,
            iconList,
            onPermission,
            ref,
            multiple,
            iconClickType,
            permission,
        });

        return (
            <div className="share-ai-power-recognition">
                {showShareIcon && (
                    // 复制链接部分
                    <div className="copy-link-section">
                        <div className="link-display">
                            <span className="link-text">{displayLink}</span>
                        </div>
                        <button className="copy-button" onClick={handleCopyLink}>
                            {lang('复制')}
                        </button>
                    </div>
                )}

                {/* 权限控制部分 */}
                <PermissionControl
                    isPublic={info.permission === CertificatePermission.PUBLIC}
                    onChange={handlePermissionChange}
                />
                {shouldShowIcons && (
                    <div className="share-ai-power-recognition-content">
                        {/* 分享图标部分 */}
                        <div
                            className={`share-icons ${
                                displayIconList.length > SHARE_ICON_SCROLL_THRESHOLD ? 'scrollable' : ''
                            }`}
                        >
                            {displayIconList.map((iconItem) => {
                                const isSelected = info.selectedIcons.includes(iconItem.key);
                                return (
                                    <ShareIcon
                                        key={iconItem.key}
                                        iconItem={iconItem}
                                        isSelected={isSelected}
                                        onClick={handleIconClick}
                                    />
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        );
    },
);

CaetShare.displayName = 'CaetShare';

const defaultData: ICaetShareDialogProps = {
    id: '',
    showShareIcon: false,
    okText: lang('确认'),
    title: lang('分享 UMU CAET 证书'),
    link: '',
};

const onDialogCaetShare = (data?: ICaetShareDialogProps) => {
    const {
        okText = lang('确认'),
        title = lang('分享 UMU CAET 证书'),
        onConfirm = defaultCaetShareConfirm,
        onSuccessCallback,
        ...rest
    } = data || defaultData;

    // 创建 ref 用于获取组件配置信息
    let shareRef: React.RefObject<ICaetShareRef> | null = null;

    const dialog = confirm({
        title,
        centered: true,
        maskClosable: true,
        closable: false,
        width: '4.5rem',
        content: (
            <CaetShare
                {...rest}
                ref={(ref) => {
                    shareRef = { current: ref };
                }}
            />
        ),
        okText,
        cancelText: false,
        onOk: async () => {
            // 通过 ref 获取配置信息
            if (shareRef?.current) {
                const shareInfo = shareRef.current.getShareInfo();
                if (shareInfo.permission !== rest.permission) {
                    const { isSuccess, message } = await onConfirm(shareInfo, rest.id);
                    if (isSuccess) {
                        onSuccessCallback?.(shareInfo);
                    } else {
                        SnackBar.show({
                            message: message || lang('证书权限设置失败'),
                        });
                    }
                    return;
                }
                onSuccessCallback?.(shareInfo);
            }
        },
    });
    return dialog;
};

export { CaetShare, onDialogCaetShare };
